import type { StateTree } from '@makemydeal/dr-dash-types';
import type { RealState } from '@makemydeal/shared-scoped-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { getBaggageFuelEconomy } from './baggageFuelEconomySelectors';
import * as vehicleSelectors from './vehicleSelectors';
import type { FuelSavingData, FuelSavingSellingVehicle } from '@makemydeal/dr-platform-types';
import { getPeriodMultiplier } from '@makemydeal/dr-activities-fuel-saving';
import type { FuelSavingPeriod } from '../types/fuelSavingTypes';

/**
 * Derives the fuel saving PDF data payload from persisted Redux state.
 *
 * Data sources:
 *  - baggageFuelEconomy.vehicles — per-selling-vehicle MPG and price/gallon
 *  - baggageFuelEconomy.trades   — per-trade MPG and shared price/gallon (used as tradePricePerGallon)
 *  - scopedState (per scope)     — vehicle year/make/model/trim + VIN for names
 *  - tradeInsSelectors           — milesDriven from included trade-ins (shared input)
 *
 * Returns `undefined` when there is no fuel economy baggage (user never opened Apply).
 */
export const getFuelSavingPdfData = (state: RealState): FuelSavingData | undefined => {
    const primaryScopeId = scopedStateSelectors.getPrimaryScopeId(state);
    const primaryState = scopedStateSelectors.getScopeStateById(state, primaryScopeId) as StateTree;

    const baggage = getBaggageFuelEconomy(primaryState);
    if (!baggage) return undefined;

    // -------------------------------------------------------------------------
    // Derive shared trade-level values
    // -------------------------------------------------------------------------
    // Compute average trade MPG from persisted baggage values (skip zeroes)
    const tradeMpgs = Object.values(baggage.trades ?? {})
        .map((entry) => entry.tradeInMpg ?? 0)
        .filter((mpg) => mpg > 0);
    // Keep raw (unrounded) value so rounding errors don't compound in the savings formula
    const averageTradeMpg = tradeMpgs.length > 0 ? tradeMpgs.reduce((sum, mpg) => sum + mpg, 0) / tradeMpgs.length : 0;

    // tradePricePerGallon — average persisted trade price across all trades (skip zeroes)
    const tradePricesPerGallon = Object.values(baggage.trades ?? {})
        .map((entry) => entry.fuelPricePerGallon ?? 0)
        .filter((price) => price > 0);
    // Keep raw (unrounded) value so rounding errors don't compound in the savings formula
    const tradePricePerGallon =
        tradePricesPerGallon.length > 0
            ? tradePricesPerGallon.reduce((sum, price) => sum + price, 0) / tradePricesPerGallon.length
            : 0;

    // milesDriven — shared across all trade comparisons (stored on the first trade)
    const firstTradeEntry = Object.values(baggage.trades ?? {})[0];
    const milesDriven = firstTradeEntry?.milesDriven ?? 0;

    // -------------------------------------------------------------------------
    // Build selling-vehicle entries — one per scope (primary + scenario scopes)
    // -------------------------------------------------------------------------
    const scopesOrder = scopedStateSelectors.getInitializedScopesOrder(state);

    const sellingVehicles = scopesOrder
        .map((scopeId) => {
            const scopeState = scopedStateSelectors.getScopeStateById(state, scopeId) as StateTree;
            const vin = vehicleSelectors.getVehicleVin(scopeState);
            if (!vin) return undefined;

            const vehicleEntry = baggage.vehicles?.[vin];
            if (!vehicleEntry) return undefined;

            const year = vehicleSelectors.getVehicleYear(scopeState);
            const make = vehicleSelectors.getVehicleMake(scopeState);
            const model = vehicleSelectors.getVehicleModel(scopeState);
            const trim = vehicleSelectors.getVehicleTrim(scopeState);
            const vehicleName = [year, make, model, trim].filter(Boolean).join(' ') || vin;

            const pricePerGallon = vehicleEntry.fuelPricePerGallon ?? tradePricePerGallon;
            const newVehicleMpg = vehicleEntry.newVehicleMpg ?? 0;

            // Resolve per-vehicle milesDriven/milesPer, falling back to shared trade values
            const vehicleMilesDriven = vehicleEntry.milesDriven ?? milesDriven;
            const normalizedMilesPer = (vehicleEntry.milesPer ?? firstTradeEntry?.milesPer ?? 'year').toUpperCase();
            const vehicleMilesPer: FuelSavingPeriod = ['WEEK', 'MONTH', 'YEAR'].includes(normalizedMilesPer)
                ? (normalizedMilesPer as FuelSavingPeriod)
                : 'YEAR';

            // Normalize to monthly: yearly ÷ 12 for YEAR, weekly × 52 ÷ 12 for WEEK, direct for MONTH
            const yearlyMiles = vehicleMilesDriven * getPeriodMultiplier(vehicleMilesPer);
            const monthlyMiles = yearlyMiles / 12;

            // savings = (monthlyMiles / avgTradeMpg) × tradePricePerGallon − (monthlyMiles / newMpg) × newPricePerGallon
            const rawMonthlySavings =
                newVehicleMpg > 0 && averageTradeMpg > 0
                    ? (monthlyMiles / averageTradeMpg) * tradePricePerGallon - (monthlyMiles / newVehicleMpg) * pricePerGallon
                    : 0;

            const monthlySavings = Math.round(rawMonthlySavings * 100) / 100;
            const yearlySavings = Math.round(rawMonthlySavings * 12 * 100) / 100;
            const threeYearsSavings = Math.round(rawMonthlySavings * 36 * 100) / 100;

            const vehicle: FuelSavingSellingVehicle = {
                vehicleName,
                vehicleVin: vin,
                mpg: Math.round(newVehicleMpg * 100) / 100,
                pricePerGallon: Math.round(pricePerGallon * 100) / 100,
                milesDriven: vehicleMilesDriven,
                milesPer: vehicleEntry.milesPer ?? firstTradeEntry?.milesPer,
                savings: {
                    monthly: monthlySavings,
                    yearly: yearlySavings,
                    threeYears: threeYearsSavings
                }
            };
            return vehicle;
        })
        .filter((v): v is FuelSavingSellingVehicle => v !== undefined);

    // Dedup by VIN — scenario scopes can carry the same selling vehicle as the primary
    // scope (or another scenario), which would otherwise print it more than once.
    // Keep the first occurrence, since scopesOrder is primary-first.
    const printVehicles = new Set<string | undefined>();
    const dedupedSellingVehicles = sellingVehicles.filter((vehicle) => {
        const vehicleData = !printVehicles.has(vehicle.vehicleVin);
        if (vehicleData) printVehicles.add(vehicle.vehicleVin);
        return vehicleData;
    });

    return {
        averageTradeMpg: Math.round(averageTradeMpg * 100) / 100,
        tradePricePerGallon: Math.round(tradePricePerGallon * 100) / 100,
        sellingVehicles: dedupedSellingVehicles
    };
};
